import { log } from '@/lib/log';
import { useAlertFeedStore } from '@/lib/store/alertFeed';
import { useAlertsStore } from '@/lib/store/alerts';
import { useDronesStore } from '@/lib/store/drones';
import { useInfraStore } from '@/lib/store/infra';
import { useRtmStore } from '@/lib/store/rtm';
import { useSurvStore } from '@/lib/store/surv';
import { useTargetsStore } from '@/lib/store/targets';
import { useWaypointProgressStore } from '@/lib/store/waypointProgress';
import { useWxStore } from '@/lib/store/wx';
import type { DroneId } from '@/types/ids';
/**
 * Wires the MQTT source into every Zustand slice.
 * Single source of truth for "MQTT → state" mapping.
 */
import { useEffect } from 'react';
import { GCS_STREAM_FIELDS } from './legacy/gcsStream';
import { getMqttSource } from './source';

export function MqttConnector() {
  useEffect(() => {
    const src = getMqttSource();
    const unsub = src.subscribe((msg) => {
      switch (msg.kind) {
        case 'telemetry':
          useDronesStore.getState().applyTelemetry(msg.payload);
          return;
        case 'c2links':
          useDronesStore.getState().applyC2(msg.payload);
          return;
        case 'proximity':
          useDronesStore.getState().applyProximity(msg.payload);
          return;
        case 'oi':
          useDronesStore.getState().applyOi(msg.payload);
          return;
        case 'events':
          useAlertsStore.getState().push(msg.payload);
          return;
        case 'alert':
          // The Live Alerts feed only. The waypoint overlay does NOT read the
          // alerts-key copy of `waypoint_approached`: it reads the identical
          // per-aircraft message (`case 'waypoint'`), same as `waypoint_pred`,
          // so applying both would count every arrival twice.
          useAlertFeedStore.getState().push(msg.payload);
          return;
        case 'waypoint':
          useWaypointProgressStore.getState().applyAlert(msg.payload);
          return;
        case 'targets':
          useTargetsStore.getState().applyTargets(msg.payload);
          return;
        case 'surveillance':
          useSurvStore.getState().apply(msg.payload);
          return;
        case 'infrastructure':
          useInfraStore.getState().apply(msg.payload);
          return;
        case 'services':
          useRtmStore.getState().apply(msg.payload);
          return;
        case 'weather':
          useWxStore.getState().setAllStations(msg.payload.stations);
          return;
      }
    });
    // The raw GCS stream (real satellites / HDOP / GPS fix, ENG-271) is heavy, so
    // it follows the selected own-fleet aircraft only; partner and Network ID
    // aircraft have no such topic on our broker.
    let followed: DroneId | null = null;
    const followSelected = () => {
      const { selectedId, byId } = useDronesStore.getState();
      const next = selectedId != null && byId[selectedId]?.origin === 'internal' ? selectedId : null;
      if (next === followed) return;
      // The aircraft we stop following keeps no stale stream readings on screen.
      if (followed) useDronesStore.getState().clearTelemetryFields(followed, GCS_STREAM_FIELDS);
      followed = next;
      src.setFocusedAircraft?.(next);
    };
    const unsubSelection = useDronesStore.subscribe((s) => s.selectedId, followSelected);
    followSelected();
    src.connect().catch((e) => log.error('MQTT connect failed', { error: String(e) }));
    // NB: deliberately don't call `src.disconnect()` here.
    //
    // React 19 StrictMode + Vite HMR double-invoke this effect in dev (mount
    // → cleanup → mount). If we tore the broker connection down on cleanup
    // we'd clear every per-drone backfill snapshot inside the source — and
    // every fused target — which made the UI flicker every reload: status
    // badges would blink "—" → "Active" and aircraft markers would visibly
    // disappear and reappear.
    //
    // The source is a process-wide singleton (`getMqttSource()`); the safe
    // contract is: connect once, stay connected for the page lifetime. The
    // only times we'd genuinely want to disconnect are Live → Mock toggle or
    // logout, and both already trigger a full page reload via `setDataSource`.
    //
    // Cleanup only detaches THIS effect's listener; the broker keeps streaming
    // to whatever listener the next mount attaches.
    return () => {
      unsub();
      unsubSelection();
    };
  }, []);
  return null;
}
